import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useGetConversationThreadQuery,
  useReplyToMessageMutation,
  useMarkAsReadMutation,
} from 'store/services/message/messageApi';
import { useMessageSocket } from 'hooks/useMessageSocket';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { ChatImageLightbox } from 'components/ChatImageLightbox/ChatImageLightbox';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { isSystemMessage, isSystemConversation } from 'utils/messageHelpers';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import type { Message, MessageImage, MessageWithReadReceipt } from 'types/interfaces/message';
import './ConversationThreadPage.css';

const MAX_IMAGES_PER_MESSAGE = 3;
const MAX_IMAGES_PER_CONVERSATION = 20;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Convert literal bullet characters (•) to proper Markdown list syntax
const convertBulletsToMarkdown = (content: string): string => {
  // Add newlines before standalone bullets that follow text
  let processed = content.replace(/([^\n])\s*•\s+/g, '$1\n- ');
  // Handle bullets at the very start of the string
  processed = processed.replace(/^•\s+/, '- ');
  return processed;
};

export default function ConversationThreadPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const [replyMessage, setReplyMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [lightboxImages, setLightboxImages] = useState<MessageImage[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const previousMessageCountRef = useRef<number>(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useGetConversationThreadQuery(conversationId!);
  const [replyToMessage, { isLoading: isReplying }] = useReplyToMessageMutation();
  const [markAsRead] = useMarkAsReadMutation();

  // Get socket for emitting mark as read
  const { emitMarkAsRead } = useMessageSocket({ enabled: true });

  // Mark as read immediately when conversation loads
  useEffect(() => {
    if (conversationId && data) {
      // Call REST API for persistence
      markAsRead(conversationId);

      // Also emit via WebSocket for real-time read receipts to sender
      const unreadMessageIds = data.messages
        .filter((m: Message) => !m.isRead && m.sender?.id === data.otherUser.id)
        .map((m: Message) => m.id);

      if (unreadMessageIds.length > 0) {
        emitMarkAsRead(conversationId, unreadMessageIds);
      }
    }
  }, [conversationId, data, markAsRead, emitMarkAsRead]);

  // Auto-scroll chat container to bottom on initial load and when new messages arrive
  // Uses scrollTop instead of scrollIntoView to avoid scrolling the entire page
  useEffect(() => {
    if (data?.messages && messagesContainerRef.current) {
      const currentMessageCount = data.messages.length;

      // Scroll on initial load (previousCount === 0) or when new message added
      if (previousMessageCountRef.current === 0 || currentMessageCount > previousMessageCountRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }

      previousMessageCountRef.current = currentMessageCount;
    }
  }, [data?.messages]);


  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = replyMessage.substring(0, start) + emoji + replyMessage.substring(end);

    setReplyMessage(newMessage);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conversationImageCount = data?.imageCount ?? 0;

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES_PER_MESSAGE - pendingImages.length;
    if (remainingSlots <= 0) {
      toast.warning(`Maximum ${MAX_IMAGES_PER_MESSAGE} images per message`);
      return;
    }

    const conversationRemaining = MAX_IMAGES_PER_CONVERSATION - conversationImageCount - pendingImages.length;
    if (conversationRemaining <= 0) {
      toast.warning(`This conversation has reached the ${MAX_IMAGES_PER_CONVERSATION} image limit`);
      return;
    }

    const filesToAdd: File[] = [];
    const newPreviewUrls: string[] = [];

    for (const file of files) {
      if (filesToAdd.length >= remainingSlots || filesToAdd.length >= conversationRemaining) {
        toast.warning(`Only ${Math.min(remainingSlots, conversationRemaining)} more image(s) can be added`);
        break;
      }

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is not supported. Only JPEG, PNG, and WEBP images are allowed.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`"${file.name}" is too large. Maximum file size is 5MB.`);
        continue;
      }

      filesToAdd.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    }

    if (filesToAdd.length > 0) {
      setPendingImages(prev => [...prev, ...filesToAdd]);
      setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  }, [pendingImages.length, conversationImageCount]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  }, [addFiles]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter((f): f is File => f !== null);

    if (imageFiles.length > 0) {
      e.preventDefault();
      addFiles(imageFiles);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleRemoveImage = useCallback((index: number) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, [imagePreviewUrls]);

  const clearPendingImages = useCallback(() => {
    imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    setPendingImages([]);
    setImagePreviewUrls([]);
  }, [imagePreviewUrls]);

  const openLightbox = useCallback((images: MessageImage[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  }, []);

  const handleReply = async () => {
    if ((!replyMessage.trim() && pendingImages.length === 0) || !conversationId) return;

    try {
      await replyToMessage({
        conversationId,
        content: replyMessage.trim(),
        ...(pendingImages.length > 0 ? { images: pendingImages } : {}),
      }).unwrap();

      setReplyMessage('');
      clearPendingImages();
      toast.success('Reply sent!');
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      // 429 errors are now handled globally in baseQuery
      // Only handle other errors here
      if (error.status !== 429) {
        toast.error(error.data?.message || 'Failed to send reply');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="conversation-page">
        <div className="conversation-loading">Loading conversation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-page">
        <div className="conversation-error">
          {(error as any).status === 404
            ? "This conversation doesn't exist or you don't have access."
            : 'Failed to load conversation. Please try again.'}
        </div>
        <button onClick={() => navigate('/inbox')} className="back-to-inbox-button">
          Back to Inbox
        </button>
      </div>
    );
  }

  if (!data) return null;

  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isSystemThread = isSystemConversation(data.otherUser.id, data.otherUser.username);

  return (
    <div className="conversation-page">
      <div className="conversation-header">
        <button onClick={() => navigate('/inbox')} className="back-button">
          ← Back to Inbox
        </button>
        <div className="conversation-user-info">
          <AvatarWithFlag
            avatarUrl={data.otherUser.avatarUrl}
            username={data.otherUser.username}
            countryCode={data.otherUser.country}
            size={40}
          />
          <span className="conversation-username">{data.otherUser.username}</span>
        </div>
      </div>

      <div className="conversation-messages" ref={messagesContainerRef}>
        {data.messages.map((message) => {
          const isSystem = isSystemMessage(message);
          return (
            <div
              key={message.id}
              className={`message-bubble ${
                isSystem
                  ? 'system-message'
                  : message.sender?.id === data.otherUser.id ? 'received' : 'sent'
              }`}
            >
              <div className="message-sender-info">
                {isSystem ? (
                  <div className="message-avatar-placeholder system-avatar">S</div>
                ) : (
                  <AvatarWithFlag
                    avatarUrl={message.sender?.avatarUrl}
                    username={message.sender?.username}
                    countryCode={message.sender?.country}
                    size={24}
                  />
                )}
                <span className="message-sender">{message.sender?.username || 'System'}</span>
                <span className="message-time">{formatMessageTime(message.createdAt)}</span>
              </div>
              <div className="message-content">
                {isSystem ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => {
                        const isExternal = href?.startsWith('http');
                        const isMailto = href?.startsWith('mailto:');

                        if (isMailto) {
                          return <a href={href}>{children}</a>;
                        }

                        if (isExternal) {
                          return (
                            <a href={href} target="_blank" rel="noopener noreferrer">
                              {children}
                            </a>
                          );
                        }

                        return <Link to={href || '#'}>{children}</Link>;
                      },
                    }}
                  >
                    {convertBulletsToMarkdown(message.content)}
                  </ReactMarkdown>
                ) : (
                  message.content
                )}
                {message.images && message.images.length > 0 && (
                  <div className="message-images">
                    {message.images.map((img, idx) => (
                      <img
                        key={img.id}
                        src={img.url}
                        alt={`Attachment ${idx + 1}`}
                        className="message-image-thumbnail"
                        loading="lazy"
                        onClick={() => openLightbox(message.images!, idx)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (!target.dataset.retried) {
                            target.dataset.retried = 'true';
                            // Presigned URL may have expired — refetch will get fresh URLs
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Read receipt for sent messages */}
              {!isSystem && message.sender?.id !== data.otherUser.id && (message as MessageWithReadReceipt).readAt && (
                <div className="message-read-receipt">
                  <span className="read-check">✓</span>
                  <span>Read {formatRelativeTime((message as MessageWithReadReceipt).readAt!)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isSystemThread && (
        <div
          className={`conversation-reply-section${isDragging ? ' drop-zone-active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="reply-form-group">
            <label className="reply-label">Reply</label>
            <textarea
              ref={textareaRef}
              className="reply-textarea"
              placeholder="Type your reply here..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              onPaste={handlePaste}
              rows={4}
              maxLength={2000}
            />
            <CharacterCounter
              currentLength={replyMessage.length}
              maxLength={2000}
              warningThreshold={1800}
            />
          </div>

          {imagePreviewUrls.length > 0 && (
            <div className="image-preview-strip">
              {imagePreviewUrls.map((url, index) => (
                <div key={url} className="image-preview-item">
                  <img src={url} alt={`Preview ${index + 1}`} className="image-preview-thumbnail" />
                  <button
                    className="image-preview-remove"
                    onClick={() => handleRemoveImage(index)}
                    type="button"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {conversationImageCount >= MAX_IMAGES_PER_CONVERSATION && (
            <div className="image-limit-warning">
              This conversation has reached the {MAX_IMAGES_PER_CONVERSATION} image limit.
            </div>
          )}

          <div className="reply-actions">
            <div className="emoji-picker-container">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                😀
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                  buttonRef={emojiButtonRef}
                />
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              className="image-attach-button"
              onClick={() => fileInputRef.current?.click()}
              type="button"
              title="Attach images"
              disabled={
                pendingImages.length >= MAX_IMAGES_PER_MESSAGE ||
                conversationImageCount >= MAX_IMAGES_PER_CONVERSATION
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>

            <MagicButton
              label={isReplying ? 'Sending...' : 'Send Reply'}
              color="purple"
              onPress={handleReply}
              isDisabled={
                (!replyMessage.trim() && pendingImages.length === 0) ||
                replyMessage.length > 2000 ||
                isReplying
              }
              fullWidth
            />
          </div>
        </div>
      )}

      {lightboxImages && (
        <ChatImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxImages(null)}
        />
      )}
    </div>
  );
}
